import React, { useRef, useEffect, useState, useCallback } from "react";
import type { UsedSheet, Placement } from "./types.ts";
import { t, type Lang } from "./i18n.ts";

const COLORS = [
  "#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#f97316", "#14b8a6", "#6366f1",
  "#d946ef", "#84cc16", "#e11d48", "#0ea5e9", "#a855f7",
  "#10b981", "#f43f5e", "#7c3aed", "#eab308", "#2563eb",
];

const SNAP_THRESHOLD = 8; // mm in material space

interface Props {
  sheet: UsedSheet;
  lang: Lang;
  onPlacementsChange: (placements: Placement[]) => void;
}

interface DragState {
  pieceIndex: number;
  offsetX: number; // mm offset from piece origin to grab point
  offsetY: number;
  lastValidX: number;
  lastValidY: number;
}

interface SnapGuide {
  axis: "x" | "y";
  value: number; // mm
}

export function InteractiveSheetCanvas({ sheet, lang, onPlacementsChange }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [placements, setPlacements] = useState<Placement[]>(() =>
    sheet.placements.map((p) => ({ ...p }))
  );
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [hoveredPiece, setHoveredPiece] = useState<number>(-1);
  const [snapGuides, setSnapGuides] = useState<SnapGuide[]>([]);
  const scaleRef = useRef(1);

  // Sync if sheet changes externally (e.g. reset)
  useEffect(() => {
    setPlacements(sheet.placements.map((p) => ({ ...p })));
    setDragState(null);
    setSnapGuides([]);
  }, [sheet.placements]);

  // Convert pixel coords to mm
  const pxToMm = useCallback((px: number) => px / scaleRef.current, []);

  // Hit test: which piece is at (mx, my) in mm?
  const hitTest = useCallback(
    (mx: number, my: number): number => {
      for (let i = placements.length - 1; i >= 0; i--) {
        const p = placements[i]!;
        if (mx >= p.x && mx <= p.x + p.width && my >= p.y && my <= p.y + p.height) {
          return i;
        }
      }
      return -1;
    },
    [placements],
  );

  // AABB overlap test
  const overlaps = useCallback(
    (a: Placement, skipIndex: number): boolean => {
      for (let i = 0; i < placements.length; i++) {
        if (i === skipIndex) continue;
        const b = placements[i]!;
        if (a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y) {
          return true;
        }
      }
      return false;
    },
    [placements],
  );

  // Calculate snap for a piece being dragged
  const calcSnap = useCallback(
    (piece: Placement, skipIndex: number): { x: number; y: number; guides: SnapGuide[] } => {
      let bestX = piece.x;
      let bestY = piece.y;
      const guides: SnapGuide[] = [];
      let bestDx = SNAP_THRESHOLD + 1;
      let bestDy = SNAP_THRESHOLD + 1;

      // Collect edges to snap to
      const xEdges: number[] = [0, sheet.width];
      const yEdges: number[] = [0, sheet.height];

      for (let i = 0; i < placements.length; i++) {
        if (i === skipIndex) continue;
        const p = placements[i]!;
        xEdges.push(p.x, p.x + p.width);
        yEdges.push(p.y, p.y + p.height);
      }

      // Piece edges
      const pieceLeft = piece.x;
      const pieceRight = piece.x + piece.width;
      const pieceTop = piece.y;
      const pieceBottom = piece.y + piece.height;

      // Snap X: check left and right edges of dragged piece against all x edges
      for (const edge of xEdges) {
        const dLeft = Math.abs(pieceLeft - edge);
        const dRight = Math.abs(pieceRight - edge);
        if (dLeft < bestDx) {
          bestDx = dLeft;
          bestX = edge;
        }
        if (dRight < bestDx) {
          bestDx = dRight;
          bestX = edge - piece.width;
        }
      }

      // Snap Y: check top and bottom edges
      for (const edge of yEdges) {
        const dTop = Math.abs(pieceTop - edge);
        const dBottom = Math.abs(pieceBottom - edge);
        if (dTop < bestDy) {
          bestDy = dTop;
          bestY = edge;
        }
        if (dBottom < bestDy) {
          bestDy = dBottom;
          bestY = edge - piece.height;
        }
      }

      // Only apply snap if within threshold
      if (bestDx > SNAP_THRESHOLD) bestX = piece.x;
      else {
        guides.push({ axis: "x", value: bestX });
        guides.push({ axis: "x", value: bestX + piece.width });
      }

      if (bestDy > SNAP_THRESHOLD) bestY = piece.y;
      else {
        guides.push({ axis: "y", value: bestY });
        guides.push({ axis: "y", value: bestY + piece.height });
      }

      return { x: bestX, y: bestY, guides };
    },
    [placements, sheet.width, sheet.height],
  );

  // Get mouse position in mm from pointer event
  const getMouseMm = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      return { mx: pxToMm(px), my: pxToMm(py) };
    },
    [pxToMm],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const { mx, my } = getMouseMm(e);
      const idx = hitTest(mx, my);
      if (idx >= 0) {
        const p = placements[idx]!;
        setDragState({
          pieceIndex: idx,
          offsetX: mx - p.x,
          offsetY: my - p.y,
          lastValidX: p.x,
          lastValidY: p.y,
        });
        (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
      }
    },
    [getMouseMm, hitTest, placements],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const { mx, my } = getMouseMm(e);

      if (!dragState) {
        // Hover detection
        const idx = hitTest(mx, my);
        setHoveredPiece(idx);
        return;
      }

      const idx = dragState.pieceIndex;
      const piece = placements[idx]!;

      // Raw position from mouse
      let newX = mx - dragState.offsetX;
      let newY = my - dragState.offsetY;

      // Clamp to sheet boundaries
      newX = Math.max(0, Math.min(sheet.width - piece.width, newX));
      newY = Math.max(0, Math.min(sheet.height - piece.height, newY));

      // Snap
      const candidate = { ...piece, x: newX, y: newY };
      const snap = calcSnap(candidate, idx);
      candidate.x = snap.x;
      candidate.y = snap.y;

      // Clamp again after snap
      candidate.x = Math.max(0, Math.min(sheet.width - candidate.width, candidate.x));
      candidate.y = Math.max(0, Math.min(sheet.height - candidate.height, candidate.y));

      // Collision check
      if (!overlaps(candidate, idx)) {
        const next = [...placements];
        next[idx] = candidate;
        setPlacements(next);
        setDragState({ ...dragState, lastValidX: candidate.x, lastValidY: candidate.y });
        setSnapGuides(snap.guides);
      } else {
        setSnapGuides([]);
      }
    },
    [dragState, getMouseMm, hitTest, placements, calcSnap, overlaps, sheet.width, sheet.height],
  );

  const handlePointerUp = useCallback(() => {
    if (dragState) {
      onPlacementsChange(placements);
    }
    setDragState(null);
    setSnapGuides([]);
  }, [dragState, placements, onPlacementsChange]);

  // Draw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const maxW = 800;
    const maxH = 400;
    const scale = Math.min(maxW / sheet.width, maxH / sheet.height);
    scaleRef.current = scale;

    const cw = Math.round(sheet.width * scale);
    const ch = Math.round(sheet.height * scale);

    const dpr = window.devicePixelRatio || 1;
    canvas.width = cw * dpr;
    canvas.height = ch * dpr;
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    ctx.scale(dpr, dpr);

    // Background
    ctx.fillStyle = "#1a1a2e";
    ctx.fillRect(0, 0, cw, ch);

    // Grid
    ctx.strokeStyle = "#ffffff08";
    ctx.lineWidth = 0.5;
    const gridStep = 100;
    for (let x = gridStep; x < sheet.width; x += gridStep) {
      ctx.beginPath();
      ctx.moveTo(x * scale, 0);
      ctx.lineTo(x * scale, ch);
      ctx.stroke();
    }
    for (let y = gridStep; y < sheet.height; y += gridStep) {
      ctx.beginPath();
      ctx.moveTo(0, y * scale);
      ctx.lineTo(cw, y * scale);
      ctx.stroke();
    }

    // Snap guide lines
    for (const guide of snapGuides) {
      ctx.strokeStyle = "#3b82f6aa";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      if (guide.axis === "x") {
        const px = guide.value * scale;
        ctx.beginPath();
        ctx.moveTo(px, 0);
        ctx.lineTo(px, ch);
        ctx.stroke();
      } else {
        const py = guide.value * scale;
        ctx.beginPath();
        ctx.moveTo(0, py);
        ctx.lineTo(cw, py);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    // Draw pieces
    for (let i = 0; i < placements.length; i++) {
      const p = placements[i]!;
      const x = p.x * scale;
      const y = p.y * scale;
      const w = p.width * scale;
      const h = p.height * scale;
      const color = COLORS[p.piece_index % COLORS.length]!;
      const isDragging = dragState?.pieceIndex === i;
      const isHovered = hoveredPiece === i && !dragState;

      // Shadow for dragged piece
      if (isDragging) {
        ctx.shadowColor = "rgba(59, 130, 246, 0.5)";
        ctx.shadowBlur = 12;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      // Fill
      ctx.fillStyle = color + (isDragging ? "ee" : "cc");
      ctx.fillRect(x, y, w, h);

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;

      // Border
      ctx.strokeStyle = isHovered || isDragging ? "#ffffff" : color;
      ctx.lineWidth = isHovered || isDragging ? 2.5 : 1.5;
      ctx.strokeRect(x, y, w, h);

      // Label
      const label = p.label || `${p.width}×${p.height}`;
      const dimText = `${p.width}×${p.height}`;
      const fontSize = Math.min(14, Math.max(8, Math.min(w, h) * 0.15));

      ctx.fillStyle = "#fff";
      ctx.font = `bold ${fontSize}px 'Inter', system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const cx = x + w / 2;
      const cy = y + h / 2;

      if (w > 30 && h > 20) {
        ctx.fillText(label, cx, cy - fontSize * 0.6, w - 4);
        ctx.font = `${fontSize * 0.8}px 'Inter', system-ui, sans-serif`;
        ctx.fillText(dimText, cx, cy + fontSize * 0.5, w - 4);
        if (p.rotated) {
          ctx.font = `${fontSize * 0.7}px 'Inter', system-ui, sans-serif`;
          ctx.fillStyle = "#ffffffaa";
          ctx.fillText(`↻ ${t("rotated", lang)}`, cx, cy + fontSize * 1.3, w - 4);
        }
      }

      // Move cursor icon on hover
      if (isHovered && w > 20 && h > 20) {
        ctx.fillStyle = "#ffffffcc";
        ctx.font = `${Math.min(16, fontSize * 1.2)}px system-ui`;
        ctx.fillText("✥", x + w - 12, y + 12);
      }
    }

    // Remnants
    for (const r of sheet.remnants) {
      const rx = r.x * scale;
      const ry = r.y * scale;
      const rw = r.width * scale;
      const rh = r.height * scale;

      ctx.fillStyle = "#22c55e15";
      ctx.fillRect(rx, ry, rw, rh);

      ctx.strokeStyle = "#22c55e66";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(rx, ry, rw, rh);
      ctx.setLineDash([]);

      const fs = Math.min(11, Math.max(7, Math.min(rw, rh) * 0.12));
      if (rw > 40 && rh > 20) {
        ctx.fillStyle = "#22c55eaa";
        ctx.font = `${fs}px 'Inter', system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`${Math.round(r.width)}×${Math.round(r.height)}`, rx + rw / 2, ry + rh / 2, rw - 4);
      }
    }

    // Sheet border
    ctx.strokeStyle = "#475569";
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, cw, ch);

    // Dimension labels
    ctx.fillStyle = "#94a3b8";
    ctx.font = `11px 'Inter', system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(`${sheet.width} mm`, cw / 2, ch + 4);
    ctx.save();
    ctx.translate(-4, ch / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textBaseline = "bottom";
    ctx.fillText(`${sheet.height} mm`, 0, 0);
    ctx.restore();
  }, [placements, sheet, lang, dragState, hoveredPiece, snapGuides]);

  const cursorStyle = dragState ? "grabbing" : hoveredPiece >= 0 ? "grab" : "default";

  return (
    <div className="overflow-x-auto pb-4">
      <canvas
        ref={canvasRef}
        className="rounded"
        style={{ maxWidth: "100%", cursor: cursorStyle, touchAction: "none" }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      {/* Piece legend */}
      <div className="flex flex-wrap gap-2 mt-3">
        {placements.map((p, i) => (
          <div key={i} className="flex items-center gap-1 text-xs">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: COLORS[p.piece_index % COLORS.length] }}
            />
            <span>
              {p.label || "?"} {p.width}×{p.height}
              {p.rotated ? ` (${t("rotated", lang)})` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
